import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import api from "../../services/api";
import swal from "sweetalert";
import moment from "moment";
import "moment/locale/pt-br";

import {
  message as messageAnt,
  Menu,
  Dropdown,
  Row,
  Col,
  Tooltip,
  Card,
  Alert,
  Typography,
  Space,
  Divider,
} from "antd";
import { UserOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

import ModalAuditUser from "../ModalAuditUser";

import {
  Table,
  MoreIcon,
  BtnsModalUser,
  ButtonCancel,
  ButtonSave,
  Form,
  ModalEditPass,
  Modal,
  LevelSpan,
  UserIdTwygoSpan,
  InfoIcon,
  DeletedBadge,
  DeletedIcon,
} from "./styles";

import { User as UserModel } from "../../models/entities/user";
import { getTokenInfo, verifyPermission } from "../../services/auth";
import TwygoCourses from "../../components/TwygoCourses";
import { UserLevel } from "../../models/enums/UserLevel";
import { UserAudit } from "../../models/entities/userAudit";


interface IProps {
  user?: UserModel | null;
  data: UserModel[];
  setUser: Dispatch<SetStateAction<UserModel | null>>;
  setVisibleEditUser: Dispatch<SetStateAction<boolean>>;
  setShouldSearch: Dispatch<SetStateAction<boolean>>;
  profiles?: UserModel[];
  showDeleted?: boolean;
}
const UserList: React.FC<IProps> = ({
  user,
  data,
  setUser,
  setShouldSearch,
  setVisibleEditUser,
  profiles,
  showDeleted = false,
}) => {
  const [isResetPasswordModalVisible, setIsResetPasswordModalVisible] =
    useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [visibleTwygoCourses, setVisibleTwygoCourses] =
    useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<UserModel | null>(null);
  const [isAuditModalVisible, setIsAuditModalVisible] = useState<boolean>(false);
  const [auditData, setAuditData] = useState<UserAudit[]>([]);
  const [loadingAudit, setLoadingAudit] = useState<boolean>(false);

  const canEditFields = () => {
    const userLevel = getTokenInfo()?.level;
    return (
      userLevel !== undefined &&
      userLevel !== null &&
      userLevel <= UserLevel.ADMIN
    );
  };
  useEffect(() => {
    if (user) {
      delete user.password;
      form.setFieldsValue({
        ...user,
        validated_email: user.email,
        profile_permission: user.profile_permission?.split(".")[1],
      });
    }
  }, [user, form]);

  const onFinishPassword = async (userId: number) => {
    await form.validateFields();
    try {
      setLoading(true);
      let payload = form.getFieldsValue();
      delete payload.validated_password;

      const oneDayAgo = moment().subtract(2, "days");
      payload.passwordExpireIn = oneDayAgo.format("YYYY-MM-DD HH:mm:ss");

      const url = userId ? `/user/${userId}` : "/user";
      await api.put(url, { ...payload });
      swal("Senha alterada com sucesso", "", "success");
      setShouldSearch(true);
    } catch (err: any) {
      setLoading(false);
      const error = err.response?.data?.message || "Falha ao trocar senha";

      swal("Oops!", Array.isArray(error) ? error.join(", ") : error, {
        icon: "error",
      });
    } finally {
      setUser(null);
      setLoading(false);
    }
  };

  const onDelete = async (
    id: number | undefined,
    image_key: string | undefined
  ) => {
    Modal.confirm({
      title: "Atenção",
      content: "Deseja realmente remover permanentemente o usuário?",
      width: 500,
      centered: true,
      okText: "Sim",
      okType: "primary",
      cancelText: "Não",

      async onOk() {
        try {
          setLoading(true);
          await api.delete(`/s3-upload/${image_key}`);
          await api.delete(`/user/${id}`);
          setShouldSearch(true);
          swal("Usuário excluído com sucesso", "", "success");
        } catch (error: any) {
          messageAnt.error(error.response.data.message);
        } finally {
          setUser(null);
          setLoading(false);
        }
      },
    });
  };

  const handleViewCourses = (record: UserModel) => {
    setSelectedUser(record);
    setVisibleTwygoCourses(true);
  };

  const handleViewAudit = async (record: UserModel) => {
    setSelectedUser(record);
    setAuditData([]);
    setIsAuditModalVisible(true);
    setLoadingAudit(true);
    try {
      const response = await api.get(`/user-audit/${record.id}`);
      setAuditData(response.data.content || []);
    } catch (err: any) {
      const error = err.response?.data?.message || "Falha ao carregar auditoria";
      messageAnt.error(Array.isArray(error) ? error.join(", ") : error);
      setIsAuditModalVisible(false);
    } finally {
      setLoadingAudit(false);
    }
  };

  const formatProfileLevel = (level) => {
    const profileLevels = {
      1: "Principal",
      2: "Geral",
      3: "Administrador",
      4: "Proprietário",
    };

    return profileLevels[level] || "Usuário básico";
  };

  const updateUserCourses = () => {
    setShouldSearch(true);
  };

  const columns = [
    {
      title: "ID do Usuário",
      dataIndex: "id",
      key: "id",
      render: (text, _) => (
        <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {text}
          {showDeleted && (
            <Tooltip title="Usuário deletado">
              <DeletedIcon twoToneColor="#ff0000" />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: "Nome",
      dataIndex: "full_name",
      key: "full_name",
      ellipsis: true,
      render: (text, _) => {
        if (showDeleted && (!text || text.trim() === "")) {
          return (
            <Tooltip title="Usuário deletado">
              <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <DeletedBadge>Deletado</DeletedBadge>
              </span>
            </Tooltip>
          );
        }
        
        return (
          <Tooltip title={text}>
            <span style={{ textTransform: "capitalize" }}>{text}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      responsive: ["md"] as any,
      width: "15%",
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Cargo",
      dataIndex: "profile",
      key: "profile",
      width: "12%",
      ellipsis: true,
      responsive: ["md"] as any,
      render: (profileId, record) => {
        const userProfile = profiles?.find(
          (profile) => profile.id === profileId
        );
        const profileName = userProfile?.name || "Não definido";
        return (
          <Tooltip title={profileName}>
            <span>{profileName}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Nivel",
      dataIndex: "level",
      key: "level",
      ellipsis: true,
      render: (text, record) => (
        <Tooltip title={formatProfileLevel(record.level)}>
          <LevelSpan>{formatProfileLevel(record.level)}</LevelSpan>
        </Tooltip>
      ),
    },
    {
      title: (
        <span>
          Magic Link{" "}
          <Tooltip title="Link de acesso direto à Universidade The Best. Quando configurado, o usuário pode acessar seus cursos sem precisar fazer login na Universidade The Best.">
            <InfoIcon />
          </Tooltip>
        </span>
      ),
      dataIndex: "magic_link_twygo",
      key: "magic_link_twygo",
      width: "8%",
      responsive: ["md"] as any,
      render: (text, record) => (
        <UserIdTwygoSpan active={record.magic_link_twygo}>
          {record.magic_link_twygo ? "Sim" : "Não"}
        </UserIdTwygoSpan>
      ),
    },
    {
      title: "Vinculado Universidade The Best",
      dataIndex: "user_twygo_id",
      key: "user_twygo_id",
      width: "15%",
      responsive: ["md"] as any,
      render: (text, record) => (
        <UserIdTwygoSpan active={record.user_twygo_id}>
          {record.user_twygo_id ? "Sim" : "Não"}
        </UserIdTwygoSpan>
      ),
    },
    {
      title: "Status Universidade The Best",
      dataIndex: "user_twygo_status",
      key: "user_twygo_status",
      width: "15%",
      responsive: ["md"] as any,
      render: (text, record) => (
        <UserIdTwygoSpan active={record.user_twygo_status}>
          {record.user_twygo_status ? "Ativo" : "Inativo"}
        </UserIdTwygoSpan>
      ),
    },
    {
      title: "Ações",
      dataIndex: "more",
      key: "more",
      render: (_, record) => {
        const isDeleted = showDeleted;
        
        return (
          <>
            <Dropdown
              overlay={
                <Menu key={record.id}>
                  {isDeleted ? (
                    verifyPermission("user.view_audit") && (
                      <Menu.Item
                        onClick={() => handleViewAudit(record)}
                        key="5"
                      >
                        <span>Ver Auditoria</span>
                      </Menu.Item>
                    )
                  ) : (
                    <>
                      {record.user_twygo_id && (
                        <Tooltip
                          title={
                            !record.user_twygo_status
                              ? "O usuário está inativo no Twygo. Solicite o cadastro deste usuário junto ao time de Sucesso do Franqueado."
                              : ""
                          }
                          placement="right"
                        >
                          <div>
                            <Menu.Item
                              onClick={() =>
                                record.user_twygo_status && handleViewCourses(record)
                              }
                              key="1"
                              disabled={!record.user_twygo_status}
                              className={
                                !record.user_twygo_status
                                  ? "disabled-item"
                                  : "enabled-item"
                              }
                            >
                              Ver Cursos
                            </Menu.Item>
                          </div>
                        </Tooltip>
                      )}
                      {verifyPermission("user.edit_user") && (
                        <Menu.Item
                          onClick={() => {
                            setUser(record);
                            setVisibleEditUser(true);
                          }}
                          key="2"
                        >
                          <span>Editar Usuário</span>
                        </Menu.Item>
                      )}
                      {verifyPermission("user.view_audit") && (
                        <Menu.Item
                          onClick={() => handleViewAudit(record)}
                          key="5"
                        >
                          <span>Ver Auditoria</span>
                        </Menu.Item>
                      )}
                      {canEditFields() && (
                        <Menu.Item
                          onClick={() => {
                            setUser(record);
                            setIsResetPasswordModalVisible(true);
                          }}
                          key="3"
                        >
                          <span>Resetar Senha</span>
                        </Menu.Item>
                      )}

                      {verifyPermission("user.delete_user") && (
                        <Menu.Item
                          onClick={() => onDelete(record.id, record.image_key)}
                          key="4"
                        >
                          <span style={{ color: "var(--red-600)" }}>
                            Remover Usuário
                          </span>
                        </Menu.Item>
                      )}
                    </>
                  )}
                </Menu>
              }
              placement="bottomLeft"
              arrow
              trigger={["click"]}
            >
              <MoreIcon />
            </Dropdown>
          </>
        );
      },
    },
  ];
  return (
    <>
      <Table
        columns={columns}
        dataSource={data.map((entity) => ({
          ...entity,
          key: entity.id,
        }))}
      />

      <ModalEditPass
        title={
          <Typography.Title level={4} style={{ margin: 0 }}>
            Resetar Senha
          </Typography.Title>
        }
        destroyOnClose={true}
        centered
        confirmLoading={loading}
        visible={isResetPasswordModalVisible}
        closable={false}
        footer={
          <BtnsModalUser>
            <ButtonCancel
              onClick={() => {
                setIsResetPasswordModalVisible(false);
              }}
            >
              Não
            </ButtonCancel>
            <ButtonSave
              type="primary"
              onClick={() => {
                onFinishPassword(user?.id || 0);
              }}
              loading={loading}
            >
              Sim, resetar senha
            </ButtonSave>
          </BtnsModalUser>
        }
      >
        <Card bordered={false}>
          <Alert
            message={<Typography.Text strong>Atenção!</Typography.Text>}
            description="Esta ação irá expirar a senha atual do usuário e forçá-lo a criar uma nova senha no próximo acesso."
            type="info"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: 16 }}
          />

          <Typography.Title level={5} style={{ textAlign: "center" }}>
            Confirmação de Reset de Senha
          </Typography.Title>

          <Card
            type="inner"
            title={
              <Space>
                <UserOutlined />
                <span>Informações do Usuário</span>
              </Space>
            }
            style={{ marginTop: 16 }}
          >
            <Row gutter={[8, 0]}>
              <Col style={{ textAlign: "right" }}>
                <Typography.Text strong>ID:</Typography.Text>
              </Col>
              <Col>
                <Typography.Text>{user?.id}</Typography.Text>
              </Col>

              <Divider style={{ margin: "8px 0" }} />

              <Col style={{ textAlign: "right" }}>
                <Typography.Text strong>Nome:</Typography.Text>
              </Col>
              <Col>
                <Typography.Text style={{ textTransform: "capitalize" }}>
                  {user?.full_name}
                </Typography.Text>
              </Col>

              <Divider style={{ margin: "8px 0" }} />

              <Col style={{ textAlign: "right" }}>
                <Typography.Text strong>Email:</Typography.Text>
              </Col>
              <Col>
                <Typography.Text>{user?.email}</Typography.Text>
              </Col>
            </Row>
          </Card>
        </Card>
      </ModalEditPass>

      <TwygoCourses
        isVisible={visibleTwygoCourses}
        onClose={() => {
          setVisibleTwygoCourses(false);
          setSelectedUser(null);
        }}
        selectedUser={selectedUser}
        onCoursesUpdated={updateUserCourses}
      />

      <ModalAuditUser
        visible={isAuditModalVisible}
        loading={loadingAudit}
        auditData={auditData}
        selectedUser={selectedUser}
        onClose={() => {
          setIsAuditModalVisible(false);
          setSelectedUser(null);
          setAuditData([]);
        }}
      />
    </>
  );
};

export default UserList;
